import * as THREE from 'three';
import { InputManager } from '../input/Input.js';
import { Entity } from '../entities/Entity.js';
import { Room } from '../level/Room.js';
import { Mech } from '../entities/Mech.js';
import { FogOfWar } from '../systems/FogOfWar.js';
import { Projectile } from '../entities/Projectile.js';
import { Pickup } from '../entities/Pickup.js';
import { Part, HeadPartData, WeaponPartData, CorePartData, TransportPartData } from '../parts/Part.js';
import { createPart, PARTS } from '../parts/PartRegistry.js';
import { checkCollisions, type CollisionEvent } from '../systems/Collision.js';
import { Sentry } from '../entities/enemies/Sentry.js';
import { Runner } from '../entities/enemies/Runner.js';
import { Heavy } from '../entities/enemies/Heavy.js';
import { Boss } from '../entities/enemies/Boss.js';
import { Enemy } from '../entities/enemies/Enemy.js';
import { getDamageDirection } from '../systems/Damage.js';
import { ImpactEffect } from '../entities/ImpactEffect.js';
import { FusionEffect } from '../entities/FusionEffect.js';
import { getFusionResult } from '../systems/Fusion.js';
import { Cover } from '../entities/Cover.js';
import { ExitDoor } from '../entities/ExitDoor.js';
import { generateRoom, generateBossRoom, getRoomConfig, type RoomLayout, type EnemyType } from '../level/Generator.js';
import { audio } from '../audio/Audio.js';

/**
 * Core game class managing the Three.js scene and fixed timestep game loop.
 *
 * Implements a fixed timestep update loop (60 updates/sec) with variable rendering.
 * This ensures deterministic physics/game logic regardless of frame rate.
 */
export class Game {
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private renderer: THREE.WebGLRenderer;
  private input: InputManager;
  private room: Room;
  private fog: FogOfWar | null = null;

  private entities: Entity[] = [];
  private entitiesToAdd: Entity[] = [];
  private entitiesToRemove: Entity[] = [];

  private lastTime: number = 0;
  private accumulator: number = 0;
  private readonly timestep: number = 1000 / 60; // 16.67ms per update (60 UPS)

  private running: boolean = false;

  // Reference to player mech for camera follow and fog of war
  // Note: This is accessed by enemies to find the player target
  playerMech: Entity | null = null;

  // ========== FLOOR/ROOM PROGRESSION SYSTEM ==========

  /** Current floor number (1-indexed) */
  private currentFloor: number = 1;

  /** Current room within the floor (1-indexed) */
  private currentRoom: number = 1;

  /** Number of rooms per floor */
  private readonly roomsPerFloor: number = 3;

  /** Reference to the exit door in the current room */
  private exitDoor: ExitDoor | null = null;

  /** Count of living enemies in the current room */
  private enemyCount: number = 0;

  /** Whether currently transitioning between rooms */
  private isTransitioning: boolean = false;

  // ========== RUN LOOP SYSTEM ==========

  /** Whether the game is currently in game over state */
  private gameOver: boolean = false;

  /** Run statistics tracking */
  private runStats = {
    floorsCleared: 0,
    roomsCleared: 0,
    enemiesKilled: 0,
    partsCollected: 0,
    bossesDefeated: 0,
    loops: 0,
  };

  // ========== SCREEN SHAKE SYSTEM ==========

  /** Current screen shake intensity */
  private shakeIntensity: number = 0;

  /** How fast shake decays per second */
  private readonly shakeDecay: number = 10;

  // ========== ROOM MESSAGE SYSTEM ==========

  /** Timer for room cleared message */
  private roomMessageTimer: number = 0;

  // ========== FUSION PAUSE SYSTEM ==========

  /** Fusion pause timer in seconds (counts down to 0) */
  private fusionPauseTimer: number = 0;

  /** Whether currently paused for fusion */
  private isFusionPausing: boolean = false;

  constructor() {
    // Initialize Three.js scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a2e);

    // Set up orthographic camera for 3/4 isometric view
    const aspect = window.innerWidth / window.innerHeight;
    const viewSize = 20; // World units visible in view
    this.camera = new THREE.OrthographicCamera(
      -viewSize * aspect / 2,
      viewSize * aspect / 2,
      viewSize / 2,
      -viewSize / 2,
      0.1,
      1000
    );

    // Position camera for slight 3/4 perspective while keeping controls intuitive
    // Nuclear Throne style: mostly top-down but with enough angle to see depth
    // Camera at y=20 (height), z=8 (slight offset) - gives ~22° tilt
    this.camera.position.set(0, 20, 8);
    this.camera.lookAt(0, 0, 0);
    // Lock rotation so camera angle stays fixed during gameplay
    this.camera.rotation.z = 0;

    // Create WebGL renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    document.body.appendChild(this.renderer.domElement);

    // Set up scene lighting
    this.setupLights();

    // Create room (20x20 default arena size)
    this.room = new Room(20, 20);
    this.scene.add(this.room.mesh);

    // Initialize input manager
    this.input = new InputManager(this.camera, this.renderer.domElement);

    // Handle window resize
    window.addEventListener('resize', () => this.onResize());
  }

  private setupLights(): void {
    // Ambient light for base illumination
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Directional light for depth perception
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 5);
    this.scene.add(directionalLight);
  }

  private onResize(): void {
    const aspect = window.innerWidth / window.innerHeight;
    const viewSize = 20;

    this.camera.left = -viewSize * aspect / 2;
    this.camera.right = viewSize * aspect / 2;
    this.camera.top = viewSize / 2;
    this.camera.bottom = -viewSize / 2;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * Start the game loop.
   */
  start(): void {
    if (this.running) return;

    this.running = true;
    this.lastTime = performance.now();
    this.loop(this.lastTime);
  }

  /**
   * Stop the game loop.
   */
  stop(): void {
    this.running = false;
  }

  /**
   * Main game loop using fixed timestep with accumulator pattern.
   *
   * This ensures update() is called at a fixed rate (60 times/sec) while
   * render() runs as fast as possible. The accumulator handles variable
   * frame times gracefully.
   */
  private loop(currentTime: number): void {
    if (!this.running) return;

    requestAnimationFrame((time) => this.loop(time));

    const deltaTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // Add frame time to accumulator
    this.accumulator += deltaTime;

    // Process fixed timestep updates
    while (this.accumulator >= this.timestep) {
      this.update(this.timestep);
      this.accumulator -= this.timestep;
    }

    // Calculate interpolation alpha for smooth rendering
    const alpha = this.accumulator / this.timestep;
    this.render(alpha);
  }

  /**
   * Add an entity to the game world.
   * The entity will be added at the start of the next update cycle.
   *
   * @param entity - Entity to add
   */
  addEntity(entity: Entity): void {
    this.entitiesToAdd.push(entity);
  }

  /**
   * Remove an entity from the game world.
   * The entity will be removed at the end of the current update cycle.
   *
   * @param entity - Entity to remove
   */
  removeEntity(entity: Entity): void {
    this.entitiesToRemove.push(entity);
  }

  /**
   * Get the input manager for querying input state.
   */
  getInput(): InputManager {
    return this.input;
  }

  /**
   * Get the room instance.
   */
  getRoom(): Room {
    return this.room;
  }

  /**
   * Set the player mech for camera follow and fog of war.
   *
   * Initializes the fog of war system based on the mech's vision radius.
   */
  setPlayerMech(mech: Entity): void {
    this.playerMech = mech;

    // Initialize fog of war if player is a Mech
    if (mech instanceof Mech) {
      const visionRadius = mech.getVisionRadius();
      this.fog = new FogOfWar(this.scene, visionRadius);
    }
  }

  /**
   * Get the current floor number.
   */
  getCurrentFloor(): number {
    return this.currentFloor;
  }

  /**
   * Get the current room number.
   */
  getCurrentRoom(): number {
    return this.currentRoom;
  }

  // ========== SCREEN SHAKE METHODS ==========

  /**
   * Trigger screen shake effect.
   *
   * @param intensity - Shake intensity (typical values: 0.2-0.5)
   */
  shake(intensity: number): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
  }

  // ========== VISUAL EFFECT METHODS ==========

  /**
   * Spawn an impact effect at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @param color - Optional color for the particles (default: yellow)
   */
  spawnImpactEffect(x: number, y: number, color?: number): void {
    const effect = new ImpactEffect(x, y, color);
    this.addEntity(effect);
  }

  /**
   * Spawn a fusion effect at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   */
  spawnFusionEffect(x: number, y: number): void {
    const effect = new FusionEffect(x, y);
    this.addEntity(effect);
  }

  /**
   * Spawn a projectile effect (explosion or chain lightning).
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @param type - Type of effect ('explosion' or 'chain')
   * @param radius - Optional radius for explosion effects
   */
  spawnProjectileEffect(x: number, y: number, type: 'explosion' | 'chain', radius?: number): void {
    if (type === 'explosion') {
      // Explosion effect: expanding ring + screen shake
      const effectRadius = radius || 2;
      // Spawn multiple impact effects in a circle for explosion visual
      const numParticles = Math.floor(effectRadius * 4);
      for (let i = 0; i < numParticles; i++) {
        const angle = (i / numParticles) * Math.PI * 2;
        const dist = effectRadius * (0.3 + Math.random() * 0.7);
        const px = x + Math.cos(angle) * dist;
        const py = y + Math.sin(angle) * dist;
        const effect = new ImpactEffect(px, py, 0xff4400); // Orange explosion
        this.addEntity(effect);
      }
      // Center flash
      const centerEffect = new ImpactEffect(x, y, 0xffff00);
      this.addEntity(centerEffect);
      // Screen shake proportional to radius
      this.shake(effectRadius * 0.15);
    } else if (type === 'chain') {
      // Chain lightning effect: bright flash at hit location
      const effect = new ImpactEffect(x, y, 0x00aaff); // Electric blue
      this.addEntity(effect);
    }
  }

  /**
   * Trigger fusion pause effect.
   *
   * Pauses game updates for the specified duration.
   *
   * @param duration - Duration of pause in seconds
   */
  private triggerFusionPause(duration: number): void {
    this.fusionPauseTimer = duration;
    this.isFusionPausing = true;
  }

  /**
   * Show screen flash effect for fusion.
   *
   * @param success - Whether fusion succeeded (cyan) or failed (red)
   */
  showFusionFlash(success: boolean = true): void {
    const flash = document.getElementById('fusion-flash');
    if (!flash) return;

    // Set color based on success/failure
    if (success) {
      flash.classList.remove('failed');
    } else {
      flash.classList.add('failed');
    }

    // Show flash
    flash.style.opacity = '0.5';

    // Fade out
    setTimeout(() => {
      flash.style.opacity = '0';
    }, 200);
  }

  // ========== PICKUP INTERACTION SYSTEM ==========

  /**
   * Find a nearby pickup within interaction radius of a position.
   *
   * @param position - The position to search from
   * @param radius - The interaction radius (default: 1.5 units)
   * @returns The nearest pickup within radius, or null if none found
   */
  findNearbyPickup(position: { x: number; y: number }, radius: number = 1.5): Pickup | null {
    let nearestPickup: Pickup | null = null;
    let nearestDist = radius;

    for (const entity of this.entities) {
      if (entity instanceof Pickup) {
        const dx = entity.position.x - position.x;
        const dy = entity.position.y - position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < nearestDist) {
          nearestDist = dist;
          nearestPickup = entity;
        }
      }
    }

    return nearestPickup;
  }

  /**
   * Find the nearest enemy to a position.
   *
   * Used by homing projectiles to find targets.
   *
   * @param x - X position to search from
   * @param y - Y position to search from
   * @returns The nearest enemy, or null if none found
   */
  findNearestEnemy(x: number, y: number): Enemy | null {
    let nearestEnemy: Enemy | null = null;
    let nearestDist = Infinity;

    for (const entity of this.entities) {
      if (entity instanceof Enemy && entity.isAlive()) {
        const dx = entity.position.x - x;
        const dy = entity.position.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < nearestDist) {
          nearestDist = dist;
          nearestEnemy = entity;
        }
      }
    }

    return nearestEnemy;
  }

  /**
   * Find all enemies within a given radius.
   *
   * @param x - X position to search from
   * @param y - Y position to search from
   * @param radius - Search radius
   * @param exclude - Optional set of entities to exclude from results
   * @returns Array of enemies within range
   */
  findEnemiesInRange(x: number, y: number, radius: number, exclude?: Set<Entity>): Enemy[] {
    const enemies: Enemy[] = [];

    for (const entity of this.entities) {
      if (entity instanceof Enemy && entity.isAlive()) {
        if (exclude && exclude.has(entity)) continue;

        const dx = entity.position.x - x;
        const dy = entity.position.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= radius) {
          enemies.push(entity);
        }
      }
    }

    return enemies;
  }

  /**
   * Deal damage to an enemy entity.
   *
   * @param target - The entity to damage (must be an Enemy)
   * @param damage - Amount of damage to deal
   */
  dealDamageToEnemy(target: Entity, damage: number): void {
    if (target instanceof Enemy) {
      target.takeDamage(damage);
    }
  }

  /**
   * Spawn a pickup at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @param part - The part to place in the pickup
   * @returns The created pickup entity
   */
  spawnPickup(x: number, y: number, part: Part): Pickup {
    const pickup = new Pickup(x, y, part);
    this.addEntity(pickup);
    return pickup;
  }

  /**
   * Spawn a pickup by part ID.
   *
   * Convenience method that creates the part from the registry.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @param partId - ID of the part to create (from PartRegistry)
   * @returns The created pickup entity
   */
  spawnPickupById(x: number, y: number, partId: string): Pickup {
    const part = createPart(partId);
    return this.spawnPickup(x, y, part);
  }

  // ========== ENEMY SPAWNING SYSTEM ==========

  /**
   * Spawn a Sentry enemy at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @returns The created Sentry entity
   */
  spawnSentry(x: number, y: number): Sentry {
    const sentry = new Sentry(x, y, this);
    this.addEntity(sentry);
    this.enemyCount++;
    return sentry;
  }

  /**
   * Spawn a Runner enemy at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @returns The created Runner entity
   */
  spawnRunner(x: number, y: number): Runner {
    const runner = new Runner(x, y, this);
    this.addEntity(runner);
    this.enemyCount++;
    return runner;
  }

  /**
   * Spawn a Heavy enemy at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @returns The created Heavy entity
   */
  spawnHeavy(x: number, y: number): Heavy {
    const heavy = new Heavy(x, y, this);
    this.addEntity(heavy);
    this.enemyCount++;
    return heavy;
  }

  /**
   * Spawn an enemy of the specified type at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @param type - Enemy type to spawn
   * @param hpMult - HP multiplier for difficulty scaling (default: 1)
   * @param dmgMult - Damage multiplier for difficulty scaling (default: 1)
   * @returns The created enemy entity
   */
  spawnEnemyByType(x: number, y: number, type: EnemyType, hpMult: number = 1, dmgMult: number = 1): Enemy {
    let enemy: Enemy;
    switch (type) {
      case 'sentry':
        enemy = this.spawnSentry(x, y);
        break;
      case 'runner':
        enemy = this.spawnRunner(x, y);
        break;
      case 'heavy':
        enemy = this.spawnHeavy(x, y);
        break;
    }
    enemy.applyMultipliers(hpMult, dmgMult);
    return enemy;
  }

  /**
   * Spawn a Boss enemy at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @returns The created Boss entity
   */
  spawnBoss(x: number, y: number): Boss {
    const boss = new Boss(x, y, this, this.currentFloor);
    this.addEntity(boss);
    this.enemyCount++;
    return boss;
  }

  /**
   * Called when an enemy is killed.
   * Decrements enemy count and checks if room is cleared.
   */
  onEnemyKilled(): void {
    this.enemyCount--;
    this.runStats.enemiesKilled++;

    if (this.enemyCount <= 0) {
      this.onRoomCleared();
    }
  }

  /**
   * Called when all enemies in a room are defeated.
   * Unlocks the exit door and provides feedback.
   */
  private onRoomCleared(): void {
    console.log('Room cleared!');

    // Play door unlock sound
    audio.doorUnlock();

    // Unlock the exit door
    if (this.exitDoor) {
      this.exitDoor.unlock();
    }

    // Show room cleared message
    this.showRoomCleared();
  }

  /**
   * Advance to the next room or floor.
   */
  private advanceRoom(): void {
    // Track room completion
    this.runStats.roomsCleared++;

    // Check if we just completed a boss room (last room of floor)
    const wasBossRoom = this.currentRoom === this.roomsPerFloor;

    this.currentRoom++;

    if (this.currentRoom > this.roomsPerFloor) {
      // Track floor completion
      this.runStats.floorsCleared++;

      // Check if we just completed floor 3 (boss floor) - start a new loop
      if (this.currentFloor === 3 && wasBossRoom) {
        this.runStats.loops++;
        this.runStats.bossesDefeated++;
        console.log(`Loop ${this.runStats.loops} complete! Starting Loop ${this.runStats.loops + 1}`);
        // Reset to floor 1 for next loop
        this.currentFloor = 1;
      } else {
        // Normal floor progression
        this.currentFloor++;
      }
      this.currentRoom = 1;
      console.log(`Advancing to Floor ${this.currentFloor}`);
    }

    // Generate new room
    this.generateRoom();
  }

  /**
   * Transition to the next room with fade effect.
   */
  private async transitionToNextRoom(): Promise<void> {
    if (this.isTransitioning) return;

    this.isTransitioning = true;

    // Play floor transition sound
    audio.floorTransition();

    // Fade to black
    await this.fadeScreen(0, 1, 0.3);

    // Advance to next room
    this.advanceRoom();

    // Fade from black
    await this.fadeScreen(1, 0, 0.3);

    this.isTransitioning = false;
  }

  /**
   * Fade the screen overlay.
   *
   * @param fromOpacity - Starting opacity (0-1)
   * @param toOpacity - Ending opacity (0-1)
   * @param duration - Duration in seconds
   */
  private async fadeScreen(fromOpacity: number, toOpacity: number, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const overlay = document.getElementById('transition-overlay');
      if (!overlay) {
        console.warn('Transition overlay not found');
        resolve();
        return;
      }

      overlay.style.opacity = fromOpacity.toString();
      overlay.style.display = 'block';

      // Use setTimeout to create animation
      const startTime = performance.now();
      const animate = (currentTime: number) => {
        const elapsed = (currentTime - startTime) / 1000; // Convert to seconds
        const progress = Math.min(elapsed / duration, 1);

        const currentOpacity = fromOpacity + (toOpacity - fromOpacity) * progress;
        overlay.style.opacity = currentOpacity.toString();

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          if (toOpacity === 0) {
            overlay.style.display = 'none';
          }
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Update the floor display in the HUD.
   */
  private updateFloorDisplay(): void {
    const el = document.getElementById('floor-display');
    if (el) {
      const loopText = this.runStats.loops > 0 ? `Loop ${this.runStats.loops + 1} - ` : '';
      el.textContent = `${loopText}Floor ${this.currentFloor} - Room ${this.currentRoom}/${this.roomsPerFloor}`;
    }
  }

  /**
   * Show the room cleared message briefly.
   */
  private showRoomCleared(): void {
    const el = document.getElementById('room-message');
    if (!el) return;

    el.textContent = 'ROOM CLEARED';
    el.style.display = 'block';
    this.roomMessageTimer = 1.5; // Show for 1.5 seconds
  }

  // ========== GAME OVER AND RESTART SYSTEM ==========

  /**
   * Called when the player dies (head destroyed).
   * Triggers game over state and shows the game over screen.
   */
  private onPlayerDeath(): void {
    this.gameOver = true;

    // Play game over sound
    audio.gameOver();

    this.showGameOverScreen();
  }

  /**
   * Show the game over screen with run statistics.
   */
  private showGameOverScreen(): void {
    const screen = document.getElementById('game-over-screen');
    if (!screen) return;

    // Update statistics
    const updateStat = (id: string, value: number) => {
      const el = document.getElementById(id);
      if (el) el.textContent = value.toString();
    };

    updateStat('stat-floors', this.runStats.floorsCleared);
    updateStat('stat-rooms', this.runStats.roomsCleared);
    updateStat('stat-enemies', this.runStats.enemiesKilled);
    updateStat('stat-parts', this.runStats.partsCollected);
    updateStat('stat-bosses', this.runStats.bossesDefeated);
    updateStat('stat-loops', this.runStats.loops);

    // Show the screen
    screen.style.display = 'flex';
  }

  /**
   * Hide the game over screen.
   */
  private hideGameOverScreen(): void {
    const screen = document.getElementById('game-over-screen');
    if (screen) {
      screen.style.display = 'none';
    }
  }

  /**
   * Handle restart input when in game over state.
   */
  private handleRestart(): void {
    if (!this.gameOver) return;
    if (!this.input.isKeyPressed('r')) return;

    this.restartRun();
  }

  /**
   * Restart the run from the beginning.
   * Resets all game state and returns player to head-only mode.
   */
  private restartRun(): void {
    // Reset game state
    this.currentFloor = 1;
    this.currentRoom = 1;
    this.gameOver = false;

    // Reset run stats
    this.runStats = {
      floorsCleared: 0,
      roomsCleared: 0,
      enemiesKilled: 0,
      partsCollected: 0,
      bossesDefeated: 0,
      loops: 0,
    };

    // Reset player to head-only
    this.resetPlayerToHeadOnly();

    // Generate first room
    this.generateRoom();

    // Hide game over screen
    this.hideGameOverScreen();

    console.log('Run restarted!');
  }

  /**
   * Reset the player mech to head-only state.
   * Removes all parts except head and restores head durability.
   */
  private resetPlayerToHeadOnly(): void {
    if (!this.playerMech || !(this.playerMech instanceof Mech)) {
      return;
    }

    // Remove all parts except head
    this.playerMech.removePart('weapon');
    this.playerMech.removePart('core');
    this.playerMech.removePart('transport');

    // Reset head durability
    const head = this.playerMech.getPart('head');
    if (head) {
      head.durability = head.data.maxDurability;
    }

    // Position will be set by generateRoom()
  }

  /**
   * Get the effective difficulty level including loop bonus.
   * Used for scaling enemy difficulty across loops.
   *
   * @returns Effective difficulty (floor + loop multiplier)
   */
  private getEffectiveDifficulty(): number {
    // Each loop adds 3 floors worth of difficulty
    return this.currentFloor + (this.runStats.loops * 3);
  }

  // ========== COVER SPAWNING SYSTEM ==========

  /**
   * Spawn a cover object at the given position.
   *
   * @param x - X position in world space
   * @param y - Y position in world space
   * @param type - Cover type to spawn
   * @returns The created Cover entity
   */
  spawnCover(x: number, y: number, type: import('../level/Generator.js').CoverType): Cover {
    const cover = new Cover(x, y, type);
    this.addEntity(cover);
    return cover;
  }

  // ========== ROOM GENERATION SYSTEM ==========

  /**
   * Clear all non-player entities from the room.
   *
   * This preserves the player mech and its state while removing:
   * - Enemies
   * - Cover
   * - Pickups
   * - Projectiles
   * - Exit door
   * - Effects
   */
  private clearRoom(): void {
    // Mark all non-player entities for removal
    for (const entity of this.entities) {
      if (entity !== this.playerMech) {
        this.removeEntity(entity);
      }
    }

    // Clear exit door reference
    this.exitDoor = null;

    // Reset enemy count
    this.enemyCount = 0;
  }

  /**
   * Generate and populate a new room based on current floor.
   *
   * This creates a new room layout and spawns entities:
   * 1. Clear all non-player entities
   * 2. Generate room layout based on floor difficulty
   * 3. Create new room geometry
   * 4. Spawn cover objects
   * 5. Spawn enemies or boss
   * 6. Spawn exit door
   * 7. Move player to entrance
   */
  private generateRoom(): void {
    // Clear existing entities (except player)
    this.clearRoom();

    // Check if this is a boss room (every 3rd room)
    const isBossRoom = this.currentRoom === 3;

    let layout: RoomLayout;

    if (isBossRoom) {
      // Generate boss room (use effective difficulty for loops)
      const effectiveDifficulty = this.getEffectiveDifficulty();
      layout = generateBossRoom(effectiveDifficulty);
      console.log(`Generating Boss Room for Floor ${this.currentFloor} (Effective Difficulty: ${effectiveDifficulty})`);
    } else {
      // Generate normal room (use effective difficulty for loops)
      const effectiveDifficulty = this.getEffectiveDifficulty();
      const config = getRoomConfig(effectiveDifficulty);
      layout = generateRoom(config);
    }

    // Remove old room mesh
    if (this.room) {
      this.scene.remove(this.room.mesh);
    }

    // Create new room
    this.room = new Room(layout.width, layout.height);
    this.scene.add(this.room.mesh);

    // Spawn cover
    for (const cover of layout.coverPositions) {
      this.spawnCover(cover.x, cover.y, cover.type);
    }

    if (isBossRoom && layout.bossSpawn) {
      // Spawn boss
      this.spawnBoss(layout.bossSpawn.x, layout.bossSpawn.y);
    } else {
      // Spawn regular enemies with difficulty scaling (including loop bonus)
      const effectiveDifficulty = this.getEffectiveDifficulty();
      const config = getRoomConfig(effectiveDifficulty);
      for (const spawn of layout.enemySpawns) {
        this.spawnEnemyByType(spawn.x, spawn.y, spawn.type, config.hpMultiplier, config.damageMultiplier);
      }
    }

    // Spawn exit door (locked until room is cleared)
    this.exitDoor = new ExitDoor(layout.exit.x, layout.exit.y);
    this.exitDoor.lock(); // Ensure it starts locked
    this.addEntity(this.exitDoor);

    // Move player to entrance
    if (this.playerMech) {
      this.playerMech.position.x = layout.entrance.x;
      this.playerMech.position.y = layout.entrance.y;
    }

    // Update HUD
    this.updateFloorDisplay();

    console.log(`Generated Floor ${this.currentFloor} - Room ${this.currentRoom}/${this.roomsPerFloor}:`, {
      size: `${layout.width}x${layout.height}`,
      cover: layout.coverPositions.length,
      enemies: layout.enemySpawns.length,
      boss: isBossRoom,
    });
  }

  /**
   * Generate and populate a new room based on floor number.
   *
   * This is the public API for initial floor generation.
   * Sets the floor number and generates the first room.
   *
   * @param floorNumber - Current floor number (affects difficulty)
   */
  generateFloor(floorNumber: number): void {
    this.currentFloor = floorNumber;
    this.currentRoom = 1;
    this.generateRoom();
  }

  /**
   * Handle interact input (E key) for pickup swapping and door interaction.
   *
   * Priority order:
   * 1. Check for exit door interaction (if unlocked)
   * 2. Check for nearby pickup
   */
  private handleInteract(): void {
    // Only process if E key was just pressed (not held)
    if (!this.input.isKeyPressed('e')) {
      return;
    }

    // Don't allow interactions during transitions
    if (this.isTransitioning) {
      return;
    }

    // Only interact if player is a Mech
    if (!this.playerMech || !(this.playerMech instanceof Mech)) {
      return;
    }

    // Check for exit door interaction first (higher priority)
    if (this.exitDoor && this.exitDoor.isUnlocked()) {
      const dx = this.playerMech.position.x - this.exitDoor.position.x;
      const dy = this.playerMech.position.y - this.exitDoor.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 2) {
        this.transitionToNextRoom();
        return;
      }
    }

    // Check for nearby pickup
    const nearbyPickup = this.findNearbyPickup(this.playerMech.position);
    if (!nearbyPickup) {
      return;
    }

    // Get the new part from the pickup
    const newPart = nearbyPickup.getPart();

    // Play pickup sound
    audio.pickup();

    // Equip the new part, getting the old part back
    const oldPart = this.playerMech.equipPart(newPart);

    // Remove the pickup from the world
    this.removeEntity(nearbyPickup);

    // Track part collection (only count if we didn't already have a part in that slot)
    if (!oldPart) {
      this.runStats.partsCollected++;
    }

    // If there was an old part, spawn a new pickup at the mech's position
    if (oldPart) {
      this.spawnPickup(this.playerMech.position.x, this.playerMech.position.y, oldPart);
    }
  }

  /**
   * Handle fuse input (F key) for part fusion.
   *
   * When the player presses F near a pickup:
   * 1. Find nearby pickup
   * 2. Get part from pickup
   * 3. Get equipped part in same slot
   * 4. Try to fuse the two parts
   * 5. If fusion succeeds:
   *    - Trigger fusion pause
   *    - Show fusion flash
   *    - Spawn fusion particle effect
   *    - Equip the fused part
   *    - Trigger mech scale pulse
   *    - Remove pickup (both parts consumed)
   * 6. If fusion fails:
   *    - Show red flash
   *    - Trigger screen shake
   */
  private handleFuse(): void {
    // Only process if F key was just pressed (not held)
    if (!this.input.isKeyPressed('f')) {
      return;
    }

    // Only fuse if player is a Mech
    if (!this.playerMech || !(this.playerMech instanceof Mech)) {
      return;
    }

    // Find nearby pickup
    const nearbyPickup = this.findNearbyPickup(this.playerMech.position);
    if (!nearbyPickup) {
      return;
    }

    // Get the pickup part
    const pickupPart = nearbyPickup.getPart();

    // Get the equipped part in the same slot
    const equippedPart = this.playerMech.getPart(pickupPart.data.slot);

    // Can't fuse if no part is equipped in that slot
    if (!equippedPart) {
      console.log('Cannot fuse: no part equipped in slot', pickupPart.data.slot);
      // Show failed fusion feedback
      this.showFusionFlash(false);
      this.shake(0.2);
      return;
    }

    // Try to fuse the two parts
    const fusionResultId = getFusionResult(equippedPart.data.id, pickupPart.data.id);

    // Fusion failed - no valid combination
    if (!fusionResultId) {
      console.log('Cannot fuse:', equippedPart.data.name, '+', pickupPart.data.name);
      // Show failed fusion feedback
      this.showFusionFlash(false);
      this.shake(0.2);
      return;
    }

    // FUSION SUCCESS!

    // Play fusion sound
    audio.fusion();

    // Trigger fusion pause
    this.triggerFusionPause(0.3);

    // Show fusion flash
    this.showFusionFlash(true);

    // Spawn fusion particle effect at mech position
    this.spawnFusionEffect(this.playerMech.position.x, this.playerMech.position.y);

    // Create the fused part
    const fusedPart = createPart(fusionResultId);

    // Equip the fused part (replaces equipped part, triggers rebuildMesh)
    this.playerMech.equipPart(fusedPart);

    // Trigger mech scale pulse animation
    this.playerMech.triggerFusionPulse();

    // Remove pickup from world (both parts consumed)
    this.removeEntity(nearbyPickup);

    console.log('Fused:', equippedPart.data.name, '+', pickupPart.data.name, '=', fusedPart.data.name);
  }

  /**
   * Update head ability visualizations on all enemies.
   *
   * Checks player's head abilities and passes them to each enemy
   * so they can show/hide HP bars, drop previews, etc.
   */
  private updateEnemyHeadAbilityVisuals(): void {
    if (!this.playerMech) return;

    // Get player's head abilities
    const head = this.playerMech.getPart('head');
    let canSeeHP = false;
    let canSeeDrops = false;

    if (head && head.data.slot === 'head') {
      const headData = head.data as HeadPartData;
      canSeeHP = headData.canSeeHP;
      canSeeDrops = headData.canSeeDrops;
    }

    // Update all enemies
    for (const entity of this.entities) {
      if (entity instanceof Enemy) {
        entity.updateHeadAbilityVisuals(canSeeHP, canSeeDrops);
      }
    }
  }

  /**
   * Process collision events between entities.
   *
   * This method checks for collisions and handles the game logic response:
   * - Projectile vs Entity: Destroy projectile, damage entity
   * - Entity vs Wall: Already handled by entity-specific logic (e.g., Room.clampPosition)
   */
  private processCollisions(): void {
    const events = checkCollisions(this.entities, this.room);

    for (const event of events) {
      if (event.type === 'entity-entity') {
        const { entityA, entityB } = event;

        // Check for projectile vs entity collision
        // Handle both orderings (projectile could be A or B)
        let projectile: Projectile | null = null;
        let target: Entity | null = null;

        if (entityA instanceof Projectile) {
          projectile = entityA;
          target = entityB;
        } else if (entityB instanceof Projectile) {
          projectile = entityB;
          target = entityA;
        }

        // If this is a projectile collision
        if (projectile && target) {
          // Don't collide with owner
          if (projectile.getOwner() === target) {
            continue;
          }

          // Skip if projectile already hit this target (for piercing)
          if (projectile.hasHit(target)) {
            continue;
          }

          // Get damage before calling onHit (onHit may destroy the projectile)
          const damage = projectile.getDamage();

          // Let the projectile handle the hit - it decides if it should be destroyed
          // This is where modifiers like piercing, splitting, chain, explosive trigger
          const shouldDestroy = projectile.onHit(target);

          // Deal damage to target
          if (target instanceof Mech) {
            const direction = getDamageDirection(
              target.position,
              target.rotation,
              projectile.position
            );

            const stillAlive = target.takeDamage(damage, direction);

            if (target === this.playerMech && stillAlive) {
              this.shake(0.3);
            }

            if (!stillAlive && target === this.playerMech) {
              console.log('GAME OVER - Player died!');
              this.onPlayerDeath();
            }
          }

          if (target instanceof Enemy) {
            target.takeDamage(damage);
          }

          if (target instanceof Cover) {
            target.takeDamage(damage);
          }

          // Only destroy if projectile says so (piercing projectiles continue)
          if (shouldDestroy) {
            this.spawnImpactEffect(projectile.position.x, projectile.position.y);
            projectile.destroy();
          }
        }
      }
      // Entity-wall collisions are handled by individual entities
      // (e.g., Mech uses Room.clampPosition, Projectile checks bounds)
    }
  }

  /**
   * Fixed timestep update for game logic.
   *
   * @param dt - Fixed delta time in milliseconds (always 16.67ms)
   */
  private update(dt: number): void {
    // Handle restart input when in game over state
    if (this.gameOver) {
      this.handleRestart();
      this.input.update(); // Must update input even in game over
      return; // Skip normal update while in game over
    }

    // Handle fusion pause
    if (this.isFusionPausing) {
      this.fusionPauseTimer -= dt / 1000;
      if (this.fusionPauseTimer <= 0) {
        this.isFusionPausing = false;
      }
      this.input.update(); // Must update input even when paused
      return; // Skip normal update while paused
    }

    // Handle interact input (E key for pickup swapping)
    this.handleInteract();

    // Handle fuse input (F key for part fusion)
    this.handleFuse();

    // Update room message timer
    if (this.roomMessageTimer > 0) {
      this.roomMessageTimer -= dt / 1000;
      if (this.roomMessageTimer <= 0) {
        const el = document.getElementById('room-message');
        if (el) {
          el.style.display = 'none';
        }
      }
    }

    // Process entities to add
    for (const entity of this.entitiesToAdd) {
      this.entities.push(entity);
      if (entity.mesh) {
        this.scene.add(entity.mesh);
      }
    }
    this.entitiesToAdd = [];

    // Update all entities
    for (const entity of this.entities) {
      entity.update(dt);
    }

    // Update head ability visualizations on enemies
    this.updateEnemyHeadAbilityVisuals();

    // Check collisions between entities
    this.processCollisions();

    // Remove destroyed entities
    for (const entity of this.entities) {
      if (entity.isDestroyed()) {
        this.removeEntity(entity);
      }
    }

    // Process entities to remove
    for (const entity of this.entitiesToRemove) {
      const index = this.entities.indexOf(entity);
      if (index !== -1) {
        this.entities.splice(index, 1);
      }
      if (entity.mesh) {
        this.scene.remove(entity.mesh);
      }
      entity.destroy();
    }
    this.entitiesToRemove = [];

    // Update input state AFTER processing - this ensures isKeyPressed works correctly
    // by tracking the previous frame's state
    this.input.update();
  }

  /**
   * Render the scene.
   *
   * @param alpha - Interpolation factor [0, 1) for smooth rendering between updates
   */
  private render(alpha: number): void {
    // Update camera to follow player mech
    if (this.playerMech) {
      const mechPos = this.playerMech.position;

      // Camera follows player with fixed offset (maintains isometric angle)
      // The camera was set up with position (0, 20, 8) looking at origin
      // So we maintain that same relative offset as the player moves
      const cameraOffsetZ = 8; // Fixed Z offset from player (set in constructor)

      // Target position: directly above player X, offset in Z for the isometric angle
      const targetX = mechPos.x;
      const targetZ = mechPos.y + cameraOffsetZ;

      // Smoothly lerp camera position - snappy but not instant
      const lerpFactor = 0.15;
      this.camera.position.x += (targetX - this.camera.position.x) * lerpFactor;
      this.camera.position.z += (targetZ - this.camera.position.z) * lerpFactor;

      // Apply screen shake if active (temporary offset, doesn't affect base position)
      if (this.shakeIntensity > 0) {
        const offsetX = (Math.random() - 0.5) * this.shakeIntensity;
        const offsetZ = (Math.random() - 0.5) * this.shakeIntensity;
        this.camera.position.x += offsetX;
        this.camera.position.z += offsetZ;

        // Decay shake intensity (convert timestep to seconds)
        const dtSec = this.timestep / 1000;
        this.shakeIntensity -= this.shakeDecay * dtSec;
        if (this.shakeIntensity < 0) {
          this.shakeIntensity = 0;
        }
      }

      // NOTE: We do NOT call camera.lookAt() here - the camera angle
      // was set once in the constructor and stays fixed. This prevents
      // the disorienting viewport rotation that was happening before.

      // Update HUD and fog if player is a Mech
      if (this.playerMech instanceof Mech) {
        this.updateHUD(this.playerMech);

        // Update fog of war to follow player and match vision radius
        if (this.fog) {
          this.fog.updatePosition(mechPos.x, mechPos.y);

          // Update vision radius if it changed (e.g., head part swapped)
          const currentVisionRadius = this.playerMech.getVisionRadius();
          this.fog.setVisionRadius(currentVisionRadius);
        }
      }
    }

    // Alpha can be used for interpolation between states
    // For now, just render the current state
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Update the HUD display with current mech stats.
   *
   * Updates:
   * - Loadout display (equipped parts in all 4 slots)
   * - HP bar (fill width and text)
   * - Energy bar (fill width and text)
   * - Pickup hint (if near a pickup)
   */
  private updateHUD(mech: Mech): void {
    // Update slot displays
    this.updateSlotDisplay('head', mech.getPart('head'));
    this.updateSlotDisplay('weapon', mech.getPart('weapon'));
    this.updateSlotDisplay('core', mech.getPart('core'));
    this.updateSlotDisplay('transport', mech.getPart('transport'));

    // Update bars
    this.updateBar('hp', mech.getHP(), mech.getMaxHP());
    this.updateBar('energy', mech.getEnergy(), mech.getMaxEnergy());

    // Update pickup hint
    this.updatePickupHint(mech);
  }

  /**
   * Update a single slot display in the loadout.
   *
   * Shows part name, key stats, and durability.
   *
   * @param slot - The slot name (head, weapon, core, transport)
   * @param part - The part equipped in this slot, or null if empty
   */
  private updateSlotDisplay(slot: string, part: Part | null): void {
    // Update the slot label
    const labelEl = document.querySelector(`#slot-${slot} .slot-label`);
    if (labelEl) {
      const slotLabel = slot.toUpperCase();
      const partName = part?.data.name ?? 'None';
      labelEl.textContent = `${slotLabel}: ${partName}`;
    }

    // Update the stats display
    const statsEl = document.getElementById(`stats-${slot}`);
    if (statsEl) {
      if (part) {
        statsEl.textContent = this.getPartStats(part);
      } else {
        statsEl.textContent = '';
      }
    }

    // Update the durability display
    const durabilityEl = document.getElementById(`durability-${slot}`);
    if (durabilityEl) {
      if (part) {
        const current = Math.floor(part.durability);
        const max = part.data.maxDurability;
        durabilityEl.textContent = `${current}/${max}`;

        // Color code based on durability percentage
        const percentage = part.durability / part.data.maxDurability;
        if (percentage > 0.5) {
          durabilityEl.style.color = '#66bb6a'; // Green
        } else if (percentage > 0.25) {
          durabilityEl.style.color = '#ffa726'; // Orange
        } else {
          durabilityEl.style.color = '#ef5350'; // Red
        }
      } else {
        durabilityEl.textContent = '--/--';
        durabilityEl.style.color = '#666';
      }
    }
  }

  /**
   * Get a compact stats string for a part based on its slot type.
   *
   * @param part - The part to get stats for
   * @returns Compact stats string
   */
  private getPartStats(part: Part): string {
    const data = part.data;

    switch (data.slot) {
      case 'head': {
        const head = data as HeadPartData;
        const abilities: string[] = [];
        if (head.canSeeHP) abilities.push('HP');
        if (head.canSeeTrails) abilities.push('Trail');
        if (head.canSeeDrops) abilities.push('Drops');
        const abilityStr = abilities.length > 0 ? abilities.join('/') : '-';
        return `[Vis:${head.visionRadius} ${abilityStr}]`;
      }
      case 'weapon': {
        const weapon = data as WeaponPartData;
        return `[${weapon.damage}dmg ${weapon.fireRate}/s ${weapon.energyCost}E]`;
      }
      case 'core': {
        const core = data as CorePartData;
        return `[${core.maxHP}HP ${core.energyRegen}E/s]`;
      }
      case 'transport': {
        const transport = data as TransportPartData;
        return `[Spd:${transport.moveSpeed} Dash:${transport.dashEnergyCost}E]`;
      }
      default:
        return '';
    }
  }

  /**
   * Get a human-readable description of a part's key effects.
   *
   * @param part - The part to describe
   * @returns Description string for the part's effects
   */
  private getPartEffectDescription(part: Part): string {
    const data = part.data;

    switch (data.slot) {
      case 'head': {
        const head = data as HeadPartData;
        const effects: string[] = [];
        if (head.canSeeHP) effects.push('See enemy HP');
        if (head.canSeeTrails) effects.push('See trails');
        if (head.canSeeDrops) effects.push('Preview drops');
        effects.push(`Vision: ${head.visionRadius}m`);
        return effects.join(', ');
      }
      case 'weapon': {
        const weapon = data as WeaponPartData;
        const effects: string[] = [];
        effects.push(`${weapon.damage} dmg`);
        effects.push(`${weapon.fireRate}/s`);
        if (weapon.projectilesPerShot > 1) effects.push(`${weapon.projectilesPerShot} shots`);
        if (weapon.spread > 0) effects.push(`${weapon.spread}° spread`);
        // Add behavior description
        if (weapon.behavior === 'homing') effects.push('Homing');
        if (weapon.behavior === 'bouncing') effects.push(`Bounces ${weapon.maxBounces || 3}x`);
        if (weapon.behavior === 'piercing') effects.push(`Pierce ${weapon.pierceCount || 2}x`);
        if (weapon.behavior === 'splitting') effects.push(`Splits into ${weapon.splitCount || 3}`);
        return effects.join(', ');
      }
      case 'core': {
        const core = data as CorePartData;
        return `${core.maxHP} HP, ${core.energyRegen} E/s regen`;
      }
      case 'transport': {
        const transport = data as TransportPartData;
        return `Speed ${transport.moveSpeed}, Dash costs ${transport.dashEnergyCost}E`;
      }
      default:
        return '';
    }
  }

  /**
   * Update a bar display (HP or Energy).
   *
   * Updates both the fill width (visual bar) and the text display.
   *
   * @param type - The bar type ('hp' or 'energy')
   * @param current - Current value
   * @param max - Maximum value
   */
  private updateBar(type: string, current: number, max: number): void {
    const fill = document.getElementById(`${type}-fill`);
    const text = document.getElementById(`${type}-text`);

    if (fill) {
      const percentage = Math.max(0, Math.min(100, (current / max) * 100));
      fill.style.width = `${percentage}%`;
    }

    if (text) {
      text.textContent = `${Math.floor(current)}/${Math.floor(max)}`;
    }
  }

  /**
   * Update the pickup hint visibility.
   *
   * Shows the hint if the player is near a pickup or unlocked door.
   * For pickups, displays:
   * - Part name and slot type
   * - Current equipped part in that slot
   * - Whether fusion is possible and the result
   *
   * @param mech - The player mech
   */
  private updatePickupHint(mech: Mech): void {
    const pickupHint = document.getElementById('pickup-hint');
    if (!pickupHint) {
      return;
    }

    // Check for nearby unlocked exit door
    let showDoorHint = false;
    if (this.exitDoor && this.exitDoor.isUnlocked()) {
      const dx = mech.position.x - this.exitDoor.position.x;
      const dy = mech.position.y - this.exitDoor.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      showDoorHint = dist < 2;
    }

    // Check if there's a nearby pickup
    const nearbyPickup = this.findNearbyPickup(mech.position);

    // Show/hide hint based on proximity
    if (showDoorHint) {
      pickupHint.innerHTML = '<span class="key-hint">E</span> Next Room';
      pickupHint.style.display = 'block';
    } else if (nearbyPickup) {
      const pickupPart = nearbyPickup.getPart();
      const slot = pickupPart.data.slot;
      const pickupName = pickupPart.data.name;
      const pickupDesc = this.getPartEffectDescription(pickupPart);
      const equippedPart = mech.getPart(slot);
      const equippedName = equippedPart?.data.name || 'None';

      // Check for fusion possibility
      let fusionHint = '';
      let fusionDesc = '';
      if (equippedPart) {
        const fusionResultId = getFusionResult(equippedPart.data.id, pickupPart.data.id);
        if (fusionResultId && PARTS[fusionResultId]) {
          const fusedPart = createPart(fusionResultId);
          fusionHint = ` <span class="key-hint">F</span> Fuse → ${PARTS[fusionResultId].name}`;
          fusionDesc = `<br><span style="color:#00ffaa;">Fusion: ${this.getPartEffectDescription(fusedPart)}</span>`;
        }
      }

      // Format with part effects visible
      pickupHint.innerHTML =
        `<strong>${pickupName}</strong> (${slot})<br>` +
        `<span style="color:#aaa;">${pickupDesc}</span><br>` +
        `Equipped: ${equippedName}` +
        ` <span class="key-hint">E</span> Swap${fusionHint}${fusionDesc}`;
      pickupHint.style.display = 'block';
    } else {
      pickupHint.style.display = 'none';
    }
  }

  /**
   * Clean up resources when destroying the game instance.
   */
  destroy(): void {
    this.stop();
    if (this.fog) {
      this.fog.destroy();
    }
    window.removeEventListener('resize', () => this.onResize());
    this.renderer.dispose();
    document.body.removeChild(this.renderer.domElement);
  }
}
